import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator, Ruler, Square, ShoppingCart, Percent, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useConfig } from "@/context/ConfigContext";
import { parsePino } from "@/lib/media";
import { fmt, SITE } from "@/lib/site";
import { goToQuote } from "@/lib/quote";

const PINO_CAT = "Madera de Pino Nacional";
const WASTES = [0, 5, 10, 15];

export const WoodCalculator = () => {
    const { addItem, MAYOREO_MIN, unitPrice } = useCart();
    const { hasWhatsApp, waHref } = useConfig();
    const nav = useNavigate();
    const [boards, setBoards] = useState([]);
    const [mode, setMode] = useState("area"); // area | pieza
    const [area, setArea] = useState("");
    const [largo, setLargo] = useState("");
    const [ancho, setAncho] = useState("");
    const [waste, setWaste] = useState(10);
    const [selectedId, setSelectedId] = useState("");

    useEffect(() => {
        api.get("/products", { params: { categoria: PINO_CAT, limit: 100, sort: "precio_asc" } })
            .then((r) => {
                setBoards(r.data.items);
                if (r.data.items[0]) setSelectedId(r.data.items[0].id);
            })
            .catch(() => {});
    }, []);

    const board = useMemo(() => boards.find((b) => b.id === selectedId), [boards, selectedId]);

    const result = useMemo(() => {
        const target = mode === "area" ? parseFloat(area) : parseFloat(largo) * parseFloat(ancho);
        if (!target || target <= 0 || !board) return null;
        const dims = parsePino(board.sku);
        if (!dims || dims.area <= 0) return null;
        const withWaste = target * (1 + waste / 100);
        const needed = Math.ceil(withWaste / dims.area);
        const isMay = needed >= MAYOREO_MIN;
        const unit = unitPrice(board, needed);
        return {
            targetArea: target,
            withWaste,
            boardArea: dims.area,
            needed,
            isMay,
            unit,
            total: Number((unit * needed).toFixed(2)),
        };
    }, [mode, area, largo, ancho, waste, board, MAYOREO_MIN, unitPrice]);

    const addToCart = () => {
        if (!result || !board) return;
        addItem(board, result.needed);
        toast.success(`${result.needed} tablas agregadas`, { description: board.nombre });
    };

    const shareWhatsApp = () => {
        if (!result || !board) return;
        const d = parsePino(board.sku);
        const resumen =
            `Proyecto: ${result.targetArea.toFixed(2)} m² (+${waste}% desperdicio = ${result.withWaste.toFixed(2)} m²)\n` +
            `Tabla: ${d ? `${d.ancho} cm × ${d.largo.toFixed(2)} m · 25 mm` : board.nombre} (${board.sku})\n` +
            `Cantidad: ${result.needed} tablas\n` +
            `Precio unitario: ${fmt(result.unit)}${result.isMay ? " (mayoreo)" : ""}\n` +
            `Total estimado: ${fmt(result.total)}`;
        if (hasWhatsApp) {
            const href = waHref(`¡Hola ${SITE.name}! 🌲 Mi cálculo de madera:\n\n${resumen}\n\nQuisiera confirmar disponibilidad. ¡Gracias!`);
            window.open(href, "_blank");
        } else {
            goToQuote(nav, {
                detalle: resumen,
                tipo: result.isMay ? "mayoreo" : "detal",
                producto: d ? `Pino ${d.ancho} cm × ${d.largo.toFixed(2)} m` : board.nombre,
                producto_sku: board.sku,
                producto_precio: fmt(result.total),
            });
        }
    };

    const inputCls = "w-full bg-white border border-linea focus:border-madera rounded-xl outline-none py-3 px-4 text-ink transition-colors";
    const modeBtn = (active) => `flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-colors ${active ? "bg-verde text-white" : "bg-white text-ink border border-linea hover:border-madera"}`;

    return (
        <div className="bg-hueso border border-linea rounded-2xl shadow-soft overflow-hidden" data-testid="wood-calculator">
            <div className="hero-gradient text-white p-6 md:p-8 flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                    <Calculator size={24} className="text-madera" />
                </div>
                <div>
                    <h3 className="font-display font-bold text-2xl">Calculadora de madera</h3>
                    <p className="text-white/70 text-sm">Calcula cuántas tablas necesitas para tu proyecto.</p>
                </div>
            </div>

            <div className="p-6 md:p-8 grid md:grid-cols-2 gap-8">
                {/* Inputs */}
                <div className="space-y-5">
                    <div className="flex gap-2">
                        <button data-testid="calc-mode-area" onClick={() => setMode("area")} className={modeBtn(mode === "area")}>
                            <Square size={16} /> Por área
                        </button>
                        <button data-testid="calc-mode-pieza" onClick={() => setMode("pieza")} className={modeBtn(mode === "pieza")}>
                            <Ruler size={16} /> Por medidas
                        </button>
                    </div>

                    {mode === "area" ? (
                        <div>
                            <label className="text-xs uppercase tracking-widest text-mut font-semibold">Área a cubrir (m²)</label>
                            <input data-testid="calc-area" type="number" min="0" step="0.01" value={area} onChange={(e) => setArea(e.target.value)} placeholder="Ej: 6.5" className={`${inputCls} mt-2`} />
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs uppercase tracking-widest text-mut font-semibold">Largo (m)</label>
                                <input data-testid="calc-largo" type="number" min="0" step="0.01" value={largo} onChange={(e) => setLargo(e.target.value)} placeholder="Ej: 2.5" className={`${inputCls} mt-2`} />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-widest text-mut font-semibold">Ancho (m)</label>
                                <input data-testid="calc-ancho" type="number" min="0" step="0.01" value={ancho} onChange={(e) => setAncho(e.target.value)} placeholder="Ej: 3" className={`${inputCls} mt-2`} />
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="text-xs uppercase tracking-widest text-mut font-semibold flex items-center gap-1.5"><Percent size={12} /> Margen de desperdicio</label>
                        <div className="flex gap-2 mt-2">
                            {WASTES.map((w) => (
                                <button key={w} data-testid={`calc-waste-${w}`} onClick={() => setWaste(w)} className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${waste === w ? "bg-madera text-white" : "bg-white border border-linea text-ink hover:border-madera"}`}>
                                    {w}%
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-widest text-mut font-semibold">Medida de tabla</label>
                        <select data-testid="calc-board" value={selectedId} onChange={(e) => setSelectedId(e.target.value)} className={`${inputCls} mt-2`}>
                            {boards.map((b) => {
                                const d = parsePino(b.sku);
                                return <option key={b.id} value={b.id}>{d ? `${d.ancho} cm × ${d.largo.toFixed(2)} m` : b.nombre} — {fmt(b.precio_usd)}</option>;
                            })}
                        </select>
                    </div>
                </div>

                {/* Result */}
                <div className="hero-gradient text-white rounded-2xl p-6 flex flex-col">
                    {result ? (
                        <>
                            <div className="space-y-2 text-sm text-white/80">
                                <div className="flex justify-between"><span>Área del proyecto</span><span>{result.targetArea.toFixed(2)} m²</span></div>
                                <div className="flex justify-between"><span>Con {waste}% desperdicio</span><span>{result.withWaste.toFixed(2)} m²</span></div>
                                <div className="flex justify-between"><span>Área por tabla</span><span>{result.boardArea.toFixed(3)} m²</span></div>
                            </div>
                            <div className="my-5 py-5 border-y border-white/15 text-center">
                                <p className="text-xs uppercase tracking-widest text-white/60">Necesitas</p>
                                <p data-testid="calc-result-needed" className="font-display font-extrabold text-6xl text-madera leading-none mt-1">{result.needed}</p>
                                <p className="text-sm text-white/70 mt-1">tablas</p>
                            </div>
                            <div className="space-y-1.5 text-sm">
                                <div className="flex justify-between"><span className="text-white/70">Precio unitario {result.isMay && <span className="text-madera">· mayoreo</span>}</span><span>{fmt(result.unit)}</span></div>
                                <div className="flex justify-between items-end pt-1">
                                    <span className="text-white/70">Total estimado</span>
                                    <span data-testid="calc-result-total" className="font-display font-extrabold text-3xl">{fmt(result.total)}</span>
                                </div>
                            </div>
                            <button data-testid="calc-add-cart" onClick={addToCart} className="mt-5 w-full bg-naranja text-white rounded-full py-4 font-semibold hover:bg-naranja-dark transition-colors flex items-center justify-center gap-2">
                                <ShoppingCart size={18} /> Agregar {result.needed} tablas al carrito
                            </button>
                            <button data-testid="calc-share" onClick={shareWhatsApp} className="mt-2 w-full border border-white/30 text-white rounded-full py-3 font-medium hover:border-madera hover:text-madera transition-colors flex items-center justify-center gap-2">
                                <MessageCircle size={16} /> {hasWhatsApp ? "Guardar y compartir por WhatsApp" : "Enviar cálculo y pedir presupuesto"}
                            </button>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-center text-white/50 gap-3 py-10">
                            <Calculator size={40} strokeWidth={1} />
                            <p className="text-sm">Ingresa las medidas de tu proyecto para ver el estimado.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
